import { useRealtimeChannelEvents } from '@/hooks/useRealtimeChannelEvents';
import queryClient, { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';
import { CustomerMessage, ModelID } from '@/types';
import { get } from '@/utils/api';
import { useQuery } from '@tanstack/react-query';
import { CustomerMessageSearchParams, featureKeys, featurePath } from '.';

type QueryFnType = typeof fetchCustomerMessages;

type UseCustomerMessagesOptions = {
  location: ModelID;
  search?: CustomerMessageSearchParams;
  config?: QueryConfig<QueryFnType>;
};

type UseRealtimeCustomerMessagesOptions = {
  location: ModelID;
  search: { customer_id: ModelID } & CustomerMessageSearchParams;
};

type RealtimeUpdate = {
  message: CustomerMessage;
  queue: string;
};

const normalizeRealtimeUpdate = (payload: RealtimeUpdate | string): RealtimeUpdate | null => {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    try {
      const parsedPayload = JSON.parse(payload);
      if (!parsedPayload || typeof parsedPayload !== 'object') {
        return null;
      }

      return parsedPayload as RealtimeUpdate;
    } catch {
      return null;
    }
  }

  return payload;
};

export const fetchCustomerMessages = (
  location: ModelID,
  search: CustomerMessageSearchParams = {},
) => get<CustomerMessage[]>(`${featurePath}?franchise_id=${location}`, search);

export const useCustomerMessages = ({
  location,
  search = {},
  config = {},
}: UseCustomerMessagesOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: featureKeys.list(location, search),
    queryFn: () => fetchCustomerMessages(location, search),
  });
};

export const useRealtimeCustomerMessages = ({
  location,
  search,
}: UseRealtimeCustomerMessagesOptions) => {
  const customerChannelId = `customer.${search.customer_id.toString()}`;
  useRealtimeChannelEvents<RealtimeUpdate>({
    kind: 'private',
    channelName: customerChannelId,
    enabled: Boolean(search.customer_id),
    events: [
      'new-customer-message',
      '.new-customer-message',
      'customer-message-status-updated',
      '.customer-message-status-updated',
    ],
    handler: (payload) => {
      const data = normalizeRealtimeUpdate(payload);

      queryClient.setQueryData(
        featureKeys.list(location, search),
        (previous: CustomerMessage[] | undefined) => {
          if (!data?.message) return previous;

          if (!previous) {
            return [data.message];
          }

          const existing = previous.find((item) => item.id === data.message.id);
          if (existing) {
            return [
              { ...existing, ...data.message },
              ...previous
                .filter((item) => item.status !== 'sending' || item.customer_message_id)
                .filter((item) => item.id !== data.message.id),
            ];
          }

          return [
            data.message,
            ...previous.filter((item) => item.status !== 'sending' || item.customer_message_id),
          ];
        },
      );
    },
  });
  // const channel = useChannel(`private-customer.${search.customer_id.toString()}`);

  // useEvent<RealtimeUpdate>(channel, 'new-customer-message', (data) => {
  //   queryClient.setQueryData(
  //     featureKeys.list(location, search),
  //     (previous: CustomerMessage[] | undefined) => {
  //       if (!data || !data.message) return previous;
  //       if (data && previous) {
  //         const existing = previous.find((item) => item.id === data.message.id);
  //         if (existing) {
  //           return [
  //             { ...existing, ...data.message },
  //             ...previous
  //               .filter((item) => item.status !== 'sending' || item.customer_message_id)
  //               .filter((item) => item.id !== data.message.id),
  //           ];
  //         } else {
  //           return [
  //             data.message,
  //             ...previous.filter((item) => item.status !== 'sending' || item.customer_message_id),
  //           ];
  //         }
  //       }
  //       return [data.message];
  //     },
  //   );
  // });

  return {
    channel: customerChannelId,
  };
};
